import { useState, useRef } from 'react';
import { SATenant } from '../../../hooks/useSuperAdmin';
import { supabase } from '../../../lib/supabase';

const SA_ACCENT = '#6366F1';
const EDGE_URL = 'https://btxhwvjrksfzuedlfgcw.supabase.co/functions/v1/create-agency';

const STATUS_STYLES: Record<string, string> = {
  Active: 'bg-emerald-100 text-emerald-700',
  Suspended: 'bg-red-100 text-red-600',
  Trial: 'bg-amber-100 text-amber-700',
  Cancelled: 'bg-slate-100 text-slate-500',
};

const PLAN_STYLES: Record<string, string> = {
  Starter: 'bg-emerald-50 text-emerald-700',
  Professional: 'bg-teal-50 text-teal-700',
  Enterprise: 'bg-indigo-50 text-indigo-700',
  Custom: 'bg-slate-100 text-slate-600',
  Trial: 'bg-amber-50 text-amber-700',
};

const PROVISION_STEPS = [
  { id: 'tenant', label: 'Creating Tenant Workspace', icon: 'ri-building-4-line', desc: 'Registering agency in platform' },
  { id: 'auth', label: 'Creating Owner Account', icon: 'ri-user-add-line', desc: 'Setting up secure authentication' },
  { id: 'profile', label: 'Assigning Plan & Permissions', icon: 'ri-shield-check-line', desc: 'Configuring role and access level' },
  { id: 'workspace', label: 'Initialising Dashboard', icon: 'ri-dashboard-3-line', desc: 'Workspace ready for first login' },
];

interface ProvisionResult {
  tenant: { id: string; name: string; slug: string; plan: string; status: string; trial_ends_at: string };
  owner: { id: string; name: string; email: string; temp_password: string };
}

interface FormState {
  agency_name: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  city: string;
  country: string;
  plan: string;
}

interface SAAgenciesProps {
  tenants: SATenant[];
  loading: boolean;
  onRefetch: () => void;
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SAAgencies({ tenants, loading, onRefetch }: SAAgenciesProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [planFilter, setPlanFilter] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [agencyAction, setAgencyAction] = useState<{ id: string; name: string; action: 'suspend' | 'reactivate' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  // Provisioning state
  const [modalStep, setModalStep] = useState<'form' | 'provisioning' | 'success'>('form');
  const [provisionStepIndex, setProvisionStepIndex] = useState(-1);
  const [provisionError, setProvisionError] = useState('');
  const [provisionResult, setProvisionResult] = useState<ProvisionResult | null>(null);
  const [pwVisible, setPwVisible] = useState(false);
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  const [form, setForm] = useState<FormState>({
    agency_name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    city: '',
    country: '',
    plan: 'Starter',
  });
  const [formErrors, setFormErrors] = useState<Partial<FormState>>({});

  const provisionIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.name.toLowerCase().includes(q) ||
      t.owner_name.toLowerCase().includes(q) ||
      t.city.toLowerCase().includes(q) ||
      t.country.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'All' || t.status === statusFilter;
    const matchPlan = planFilter === 'All' || t.plan === planFilter;
    return matchSearch && matchStatus && matchPlan;
  });

  const totalActiveMRR = tenants.filter((t) => t.status === 'Active').reduce((s, t) => s + t.mrr, 0);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    setSaving(true);
    const newStatus = currentStatus === 'Active' ? 'Suspended' : 'Active';
    const { error } = await supabase.from('tenants').update({ status: newStatus }).eq('id', id);
    setSaving(false);
    setAgencyAction(null);
    if (error) {
      showToast('Failed to update status');
    } else {
      showToast(`Agency ${newStatus === 'Active' ? 'reactivated' : 'suspended'} successfully`);
      onRefetch();
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormState> = {};
    if (!form.agency_name.trim()) errors.agency_name = 'Required';
    if (!form.owner_name.trim()) errors.owner_name = 'Required';
    if (!form.owner_email.trim()) errors.owner_email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)) errors.owner_email = 'Invalid email';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const runProvisioning = async () => {
    setModalStep('provisioning');
    setProvisionStepIndex(0);
    setProvisionError('');

    // Simulate step progression with real API call
    const stepDelay = (ms: number) => new Promise((res) => setTimeout(res, ms));

    try {
      // Animate step 0 start
      await stepDelay(600);
      setProvisionStepIndex(1);
      await stepDelay(500);
      setProvisionStepIndex(2);
      await stepDelay(400);

      // Call Edge Function
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          agency_name: form.agency_name.trim(),
          owner_name: form.owner_name.trim(),
          owner_email: form.owner_email.trim(),
          owner_phone: form.owner_phone.trim(),
          city: form.city.trim(),
          country: form.country.trim(),
          plan: form.plan,
          status: 'Trial',
        }),
      });

      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Provisioning failed');
      }

      setProvisionStepIndex(3);
      await stepDelay(500);
      setProvisionStepIndex(4);
      await stepDelay(400);

      setProvisionResult(json as ProvisionResult);
      setModalStep('success');
      onRefetch();
    } catch (err: any) {
      setProvisionError(err?.message || 'Something went wrong. Please try again.');
      setModalStep('form');
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    runProvisioning();
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [key]: false })), 2000);
    });
  };

  const resetModal = () => {
    setShowAddModal(false);
    setModalStep('form');
    setProvisionStepIndex(-1);
    setProvisionError('');
    setProvisionResult(null);
    setPwVisible(false);
    setCopied({});
    setForm({ agency_name: '', owner_name: '', owner_email: '', owner_phone: '', city: '', country: '', plan: 'Starter' });
    setFormErrors({});
    if (provisionIntervalRef.current) clearTimeout(provisionIntervalRef.current);
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 bg-slate-800 text-white text-sm px-4 py-2.5 rounded-xl">
          {toast}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agencies..."
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-600 placeholder-slate-400 focus:outline-none focus:border-slate-300 focus:bg-white"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['All', 'Active', 'Trial', 'Suspended', 'Cancelled'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                statusFilter === s ? 'text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
              style={statusFilter === s ? { backgroundColor: SA_ACCENT } : {}}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['All', 'Starter', 'Professional', 'Enterprise', 'Trial'].map((p) => (
            <button
              key={p}
              onClick={() => setPlanFilter(p)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                planFilter === p ? 'text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
              style={planFilter === p ? { backgroundColor: SA_ACCENT } : {}}
            >
              {p}
            </button>
          ))}
        </div>
        <button
          onClick={() => { setShowAddModal(true); setModalStep('form'); }}
          className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: SA_ACCENT }}
        >
          <i className="ri-add-line" />
          Add Agency
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between">
          <p className="text-sm font-bold text-slate-800">
            All Agencies <span className="text-slate-400 font-normal">({filtered.length})</span>
          </p>
          <p className="text-xs text-slate-400">
            Total Active MRR:{' '}
            <span className="font-bold text-slate-700">AED {totalActiveMRR.toLocaleString()}</span>
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-building-4-line text-4xl text-slate-200 mb-3" />
            <p className="text-sm font-semibold text-slate-400">No agencies found</p>
            <p className="text-xs text-slate-300 mt-1">Try adjusting your filters or add a new agency</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-400 uppercase tracking-wider">
                <th className="text-left px-6 py-3 font-medium">Agency</th>
                <th className="text-left px-4 py-3 font-medium">Owner</th>
                <th className="text-left px-4 py-3 font-medium">Plan</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Staff</th>
                <th className="text-right px-4 py-3 font-medium">Bookings MTD</th>
                <th className="text-right px-4 py-3 font-medium">MRR</th>
                <th className="text-left px-4 py-3 font-medium">Since</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: t.brand_color || SA_ACCENT }}
                      >
                        {initials(t.name)}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                        <p className="text-xs text-slate-400">
                          {[t.city, t.country].filter(Boolean).join(', ') || 'Location not set'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm text-slate-700 font-medium">{t.owner_name || '—'}</p>
                    <p className="text-xs text-slate-400">{t.owner_email || t.email || '—'}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${PLAN_STYLES[t.plan] || 'bg-slate-100 text-slate-500'}`}>
                      {t.plan || 'Trial'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[t.status] || 'bg-slate-100 text-slate-500'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right text-slate-700 font-semibold">{t.user_count}</td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="font-bold text-slate-800">{t.bookings_mtd > 0 ? t.bookings_mtd.toLocaleString() : '—'}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    {t.mrr > 0 ? (
                      <span className="font-bold text-emerald-700">AED {t.mrr.toLocaleString()}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">{t.status === 'Trial' ? 'Trial' : '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-400">{formatDate(t.created_at)}</td>
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <button
                        onClick={() =>
                          setAgencyAction({ id: t.id, name: t.name, action: t.status === 'Active' ? 'suspend' : 'reactivate' })
                        }
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 cursor-pointer"
                      >
                        <i className={`text-sm ${t.status === 'Active' ? 'ri-forbid-line text-red-400' : 'ri-restart-line text-emerald-500'}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Confirm Toggle Status Modal ---- */}
      {agencyAction && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${agencyAction.action === 'suspend' ? 'bg-red-50' : 'bg-emerald-50'}`}>
                <i className={`text-xl ${agencyAction.action === 'suspend' ? 'ri-forbid-line text-red-500' : 'ri-restart-line text-emerald-500'}`} />
              </div>
              <div>
                <p className="font-bold text-slate-800">{agencyAction.action === 'suspend' ? 'Suspend Agency' : 'Reactivate Agency'}</p>
                <p className="text-xs text-slate-400">{agencyAction.name}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              {agencyAction.action === 'suspend'
                ? 'All users in this agency will lose access immediately. Their data will be preserved.'
                : 'This agency will regain full access to the platform and their data.'}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setAgencyAction(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer">
                Cancel
              </button>
              <button
                disabled={saving}
                onClick={() => handleToggleStatus(agencyAction.id, agencyAction.action === 'suspend' ? 'Active' : 'Suspended')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer disabled:opacity-60 ${agencyAction.action === 'suspend' ? 'bg-red-500' : 'bg-emerald-500'}`}
              >
                {saving ? 'Saving...' : agencyAction.action === 'suspend' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Add Agency Modal ---- */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-auto overflow-hidden">

            {/* FORM STEP */}
            {modalStep === 'form' && (
              <>
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Create New Agency</h3>
                    <p className="text-xs text-slate-400 mt-0.5">Provisions a full workspace — tenant, owner account & dashboard</p>
                  </div>
                  <button onClick={resetModal} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer">
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                {/* What will be created */}
                <div className="px-6 py-3 bg-indigo-50/60 border-b border-indigo-100/60">
                  <div className="flex items-center gap-5 text-xs text-indigo-600 font-medium">
                    {PROVISION_STEPS.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${s.icon} text-sm`} />
                        </div>
                        <span>{['Tenant', 'Auth User', 'Plan', 'Workspace'][i]}</span>
                        {i < 3 && <i className="ri-arrow-right-s-line text-indigo-300 ml-1" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {provisionError && (
                    <div className="flex items-start gap-2.5 p-3.5 bg-red-50 rounded-xl border border-red-100">
                      <i className="ri-error-warning-line text-red-500 mt-0.5" />
                      <p className="text-xs text-red-600">{provisionError}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Agency Details</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Agency Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.agency_name}
                          onChange={(e) => { setForm((f) => ({ ...f, agency_name: e.target.value })); setFormErrors((fe) => ({ ...fe, agency_name: '' })); }}
                          placeholder="e.g. Blue Ocean Travel"
                          className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-indigo-300 transition-colors ${formErrors.agency_name ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                        />
                        {formErrors.agency_name && <p className="text-xs text-red-500 mt-1">{formErrors.agency_name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                          placeholder="e.g. Dubai"
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-300"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Country</label>
                        <input
                          type="text"
                          value={form.country}
                          onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                          placeholder="e.g. UAE"
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-300"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Subscription Plan</label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Starter', 'Professional', 'Enterprise'].map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, plan: p }))}
                              className={`py-2 rounded-xl text-xs font-semibold cursor-pointer border transition-all ${
                                form.plan === p
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Owner Account</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.owner_name}
                          onChange={(e) => { setForm((f) => ({ ...f, owner_name: e.target.value })); setFormErrors((fe) => ({ ...fe, owner_name: '' })); }}
                          placeholder="Ahmed Al Rashid"
                          className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-indigo-300 transition-colors ${formErrors.owner_name ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                        />
                        {formErrors.owner_name && <p className="text-xs text-red-500 mt-1">{formErrors.owner_name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                        <input
                          type="text"
                          value={form.owner_phone}
                          onChange={(e) => setForm((f) => ({ ...f, owner_phone: e.target.value }))}
                          placeholder="+971 50 000 0000"
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-300"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          value={form.owner_email}
                          onChange={(e) => { setForm((f) => ({ ...f, owner_email: e.target.value })); setFormErrors((fe) => ({ ...fe, owner_email: '' })); }}
                          placeholder="owner@agency.com"
                          className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-indigo-300 transition-colors ${formErrors.owner_email ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                        />
                        {formErrors.owner_email && <p className="text-xs text-red-500 mt-1">{formErrors.owner_email}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-5 flex gap-3">
                  <button onClick={resetModal} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    style={{ backgroundColor: SA_ACCENT }}
                  >
                    <i className="ri-rocket-line" />
                    Provision Agency
                  </button>
                </div>
              </>
            )}

            {/* PROVISIONING STEP */}
            {modalStep === 'provisioning' && (
              <div className="px-6 py-8">
                <div className="text-center mb-8">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: SA_ACCENT }}
                  >
                    <div className="w-7 h-7 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-base">Provisioning Workspace</h3>
                  <p className="text-xs text-slate-400 mt-1">Setting up <strong className="text-slate-600">{form.agency_name}</strong> — please wait</p>
                </div>

                <div className="space-y-3">
                  {PROVISION_STEPS.map((step, i) => {
                    const isDone = provisionStepIndex > i;
                    const isActive = provisionStepIndex === i;
                    const isPending = provisionStepIndex < i;
                    return (
                      <div
                        key={step.id}
                        className={`flex items-center gap-4 p-3.5 rounded-xl border transition-all duration-300 ${
                          isDone
                            ? 'bg-emerald-50 border-emerald-100'
                            : isActive
                            ? 'bg-indigo-50 border-indigo-200'
                            : 'bg-slate-50 border-slate-100'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isDone ? 'bg-emerald-500' : isActive ? '' : 'bg-slate-200'
                        }`}
                          style={isActive ? { backgroundColor: SA_ACCENT } : {}}
                        >
                          {isDone ? (
                            <i className="ri-check-line text-white text-base" />
                          ) : isActive ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <i className={`${step.icon} text-slate-400 text-sm`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isDone ? 'text-emerald-700' : isActive ? 'text-indigo-700' : 'text-slate-400'}`}>
                            {step.label}
                          </p>
                          <p className={`text-xs mt-0.5 ${isDone ? 'text-emerald-500' : isActive ? 'text-indigo-400' : 'text-slate-300'}`}>
                            {step.desc}
                          </p>
                        </div>
                        {isDone && (
                          <span className="text-xs font-semibold text-emerald-600 whitespace-nowrap">Done</span>
                        )}
                        {isActive && (
                          <span className="text-xs font-semibold whitespace-nowrap" style={{ color: SA_ACCENT }}>Running...</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="text-center text-xs text-slate-400 mt-6">This usually takes 3–5 seconds</p>
              </div>
            )}

            {/* SUCCESS STEP */}
            {modalStep === 'success' && provisionResult && (
              <>
                <div className="px-6 pt-6 pb-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-white text-2xl" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-base">Agency Provisioned!</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    <strong className="text-slate-600">{provisionResult.tenant.name}</strong> is fully set up and ready to use
                  </p>
                </div>

                {/* Step summary */}
                <div className="mx-6 mb-4 grid grid-cols-4 gap-2">
                  {PROVISION_STEPS.map((s) => (
                    <div key={s.id} className="flex flex-col items-center gap-1 py-2.5 bg-emerald-50 rounded-xl">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${s.icon} text-emerald-600 text-sm`} />
                      </div>
                      <i className="ri-check-line text-emerald-500 text-xs" />
                    </div>
                  ))}
                </div>

                {/* Tenant info */}
                <div className="mx-6 mb-4 p-4 bg-slate-50 rounded-xl space-y-2.5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Workspace Details</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Tenant ID</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-700 bg-white px-2 py-1 rounded-lg border border-slate-100 max-w-[180px] truncate">
                        {provisionResult.tenant.id}
                      </span>
                      <button onClick={() => copyToClipboard(provisionResult.tenant.id, 'tid')} className="w-6 h-6 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600">
                        <i className={copied.tid ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Plan</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${PLAN_STYLES[provisionResult.tenant.plan] || 'bg-slate-100'}`}>
                      {provisionResult.tenant.plan}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Trial Ends</span>
                    <span className="text-xs text-slate-600 font-medium">
                      {new Date(provisionResult.tenant.trial_ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Credentials */}
                <div className="mx-6 mb-5 p-4 bg-amber-50 rounded-xl border border-amber-100 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-key-2-line text-amber-600" />
                    <p className="text-xs font-bold text-amber-700">Login Credentials — Share with Agency Owner</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-600">Email</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-700">{provisionResult.owner.email}</span>
                      <button onClick={() => copyToClipboard(provisionResult.owner.email, 'email')} className="w-6 h-6 flex items-center justify-center cursor-pointer text-amber-400 hover:text-amber-600">
                        <i className={copied.email ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-600">Password</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-700 bg-white px-2 py-1 rounded-lg border border-amber-100">
                        {pwVisible ? provisionResult.owner.temp_password : '••••••••••••'}
                      </span>
                      <button onClick={() => setPwVisible((v) => !v)} className="w-6 h-6 flex items-center justify-center cursor-pointer text-amber-400 hover:text-amber-600">
                        <i className={pwVisible ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </button>
                      <button onClick={() => copyToClipboard(provisionResult.owner.temp_password, 'pw')} className="w-6 h-6 flex items-center justify-center cursor-pointer text-amber-400 hover:text-amber-600">
                        <i className={copied.pw ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-amber-500 mt-1">
                    <i className="ri-information-line mr-1" />
                    Ask the owner to change their password on first login
                  </p>
                </div>

                <div className="px-6 pb-6">
                  <button
                    onClick={resetModal}
                    className="w-full py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
                    style={{ backgroundColor: SA_ACCENT }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
